import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useColumnPreferences, type ColumnConfig as ColumnConfigType, type ColumnDefinition } from '@/lib/columns';
import { FilterBar, useFilters } from '@/lib/filters';
import {
  ViewSelector,
  SaveViewDialog,
  useViews,
  type View,
  viewFilterToCondition,
} from '@/lib/views';
import { ContentViewModal } from '@/components/ContentViewModal';
import { SyncStatusPanel } from '@/components/SyncStatusPanel';
import { EditSiteModal } from '@/components/EditSiteModal';
import { InlineEditModal } from '@/components/InlineEditModal';
import { ContentEditorModal } from '@/components/ContentEditorModal';
import { BulkEditModal } from '@/components/BulkEditModal';
import { stripHtml, countCharacters, truncateText, decodeHtmlEntities } from '@/lib/utils';
import { useUsage, canSync } from '@/hooks/useUsage';
import { useTaxonomyLookup } from '@/hooks/useTaxonomyLookup';
import { useAuthors } from '@/hooks/useAuthors';

// Format relative time (e.g., "2 hours ago", "3 days ago")
function formatRelativeTime(dateString: string): { relative: string; absolute: string } {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffMonths = Math.floor(diffDays / 30);

  let relative: string;
  if (diffMinutes < 1) {
    relative = 'Just now';
  } else if (diffMinutes < 60) {
    relative = `${diffMinutes} min${diffMinutes === 1 ? '' : 's'} ago`;
  } else if (diffHours < 24) {
    relative = `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  } else if (diffDays < 30) {
    relative = `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  } else if (diffMonths < 12) {
    relative = `${diffMonths} month${diffMonths === 1 ? '' : 's'} ago`;
  } else {
    relative = date.toLocaleDateString();
  }

  const absolute = date.toLocaleString();
  return { relative, absolute };
}

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debouncedValue;
}

interface SiteViewProps {
  siteId: string;
  siteName: string;
  siteUrl: string;
  wpUsername: string | null;
  enabledContentTypes: string[];
  activePlugin: string;
  activeContentType: string;
  onBack: () => void;
  onPluginChange: (plugin: string) => void;
  onContentTypeChange: (contentType: string) => void;
}

interface SubTab {
  id: string;
  label: string;
  endpoint: string;
  comingSoon?: boolean;
}

interface PrimaryTab {
  id: string;
  label: string;
  comingSoon?: boolean;
  subtabs: SubTab[];
}

const PRIMARY_TABS: PrimaryTab[] = [
  {
    id: 'wordpress',
    label: 'WordPress',
    subtabs: [
      { id: 'posts', label: 'Posts', endpoint: '/wp/v2/posts' },
      { id: 'pages', label: 'Pages', endpoint: '/wp/v2/pages' },
      { id: 'media', label: 'Media', endpoint: '/wp/v2/media' },
      { id: 'users', label: 'Users', endpoint: '/wp/v2/users', comingSoon: true },
      { id: 'comments', label: 'Comments', endpoint: '/wp/v2/comments', comingSoon: true },
      { id: 'categories', label: 'Categories', endpoint: '/wp/v2/categories', comingSoon: true },
      { id: 'tags', label: 'Tags', endpoint: '/wp/v2/tags', comingSoon: true },
    ],
  },
  {
    id: 'woocommerce',
    label: 'WooCommerce',
    comingSoon: true,
    subtabs: [
      { id: 'products', label: 'Products', endpoint: '/wc/v3/products' },
      { id: 'orders', label: 'Orders', endpoint: '/wc/v3/orders' },
      { id: 'coupons', label: 'Coupons', endpoint: '/wc/v3/coupons' },
    ],
  },
  {
    id: 'acf',
    label: 'ACF',
    comingSoon: true,
    subtabs: [
      { id: 'field_groups', label: 'Field Groups', endpoint: '/acf/v3/field-groups' },
    ],
  },
  {
    id: 'gravity_forms',
    label: 'Gravity Forms',
    comingSoon: true,
    subtabs: [
      { id: 'entries', label: 'Entries', endpoint: '/gf/v2/entries' },
      { id: 'forms', label: 'Forms', endpoint: '/gf/v2/forms' },
    ],
  },
];

export default function SiteView({
  siteId,
  siteName,
  siteUrl,
  wpUsername,
  enabledContentTypes,
  activePlugin,
  activeContentType,
  onBack,
  onPluginChange,
  onContentTypeChange,
}: SiteViewProps) {
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<{ type: 'success' | 'error' | 'warning'; text: string } | null>(null);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSyncStatus, setShowSyncStatus] = useState(false);
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);

  // Usage tracking for sync limits
  const { data: usage } = useUsage();
  const syncCheck = canSync(usage);

  // Use URL-provided values for active tabs
  const activePrimaryTab = activePlugin;
  const activeSubtab = activeContentType;

  const currentPrimaryTab = PRIMARY_TABS.find(t => t.id === activePrimaryTab) || PRIMARY_TABS[0];
  const currentSubtab = currentPrimaryTab.subtabs.find(t => t.id === activeSubtab);

  // Filter subtabs to only show enabled content types OR coming soon subtabs
  const visibleSubtabs = currentPrimaryTab.subtabs.filter(
    subtab => currentPrimaryTab.comingSoon || subtab.comingSoon || enabledContentTypes.includes(subtab.id)
  );

  const handleSync = async () => {
    // Check usage limits before syncing
    if (!syncCheck.allowed) {
      setSyncMessage({ type: 'error', text: syncCheck.message || 'Record limit reached' });
      return;
    }

    setSyncing(true);
    setSyncMessage(null);
    try {
      const result = await api.post<{ success: boolean; jobId: string; message: string; status: string }>(
        `/api/v1/sites/${siteId}/sync`,
        {}
      );
      setCurrentJobId(result.jobId);
      setShowSyncStatus(true); // Auto-open status panel

      // Show warning if approaching limit, success otherwise
      if (syncCheck.message) {
        setSyncMessage({ type: 'warning', text: `${result.message} (${syncCheck.message})` });
      } else {
        setSyncMessage({ type: 'success', text: result.message });
      }
    } catch (err) {
      setSyncMessage({ type: 'error', text: err instanceof Error ? err.message : 'Sync failed' });
    } finally {
      setSyncing(false);
    }
  };

  const handleDeleteSite = async () => {
    if (!confirm(`Are you sure you want to delete "${siteName}" and all its synced data? This cannot be undone.`)) {
      return;
    }

    setDeleting(true);
    try {
      await api.delete(`/api/v1/sites/${siteId}`);
      onBack(); // Go back to dashboard after deletion
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete site');
    } finally {
      setDeleting(false);
      setShowSettingsMenu(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setSyncMessage(null);
    try {
      const result = await api.post<{ success: boolean; user?: { name: string }; siteName?: string; error?: string; message?: string }>(
        `/api/v1/sites/${siteId}/test-connection`,
        {}
      );
      if (result.success) {
        setSyncMessage({
          type: 'success',
          text: `Connected to ${result.siteName || siteName} as ${result.user?.name || 'user'}`,
        });
      } else {
        setSyncMessage({
          type: 'error',
          text: result.message || result.error || 'Connection failed',
        });
      }
    } catch (err) {
      setSyncMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Connection test failed',
      });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header with site info and back button */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">{siteName}</h1>
              <p className="text-sm text-gray-500">{siteUrl}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {syncMessage && (
              <span className={`text-sm ${
                syncMessage.type === 'success' ? 'text-green-600' :
                syncMessage.type === 'warning' ? 'text-yellow-600' :
                'text-red-600'
              }`}>
                {syncMessage.text}
              </span>
            )}
            <button
              onClick={handleTestConnection}
              disabled={testing}
              className="px-3 py-1.5 text-sm flex items-center gap-1 disabled:opacity-50 text-gray-600 hover:text-gray-900"
              title="Test WordPress connection"
            >
              <svg className={`w-4 h-4 ${testing ? 'animate-pulse' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {testing ? 'Testing...' : 'Test'}
            </button>
            <button
              onClick={handleSync}
              disabled={syncing || !syncCheck.allowed}
              title={!syncCheck.allowed ? syncCheck.message : undefined}
              className={`px-3 py-1.5 text-sm flex items-center gap-1 disabled:opacity-50 ${
                !syncCheck.allowed
                  ? 'text-red-500 cursor-not-allowed'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <svg className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {syncing ? 'Syncing...' : !syncCheck.allowed ? 'Limit Reached' : 'Sync'}
            </button>
            <button
              onClick={() => setShowSyncStatus(true)}
              className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
              title="View sync status and logs"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
              Status
            </button>
            <div className="relative">
              <button
                onClick={() => setShowSettingsMenu(!showSettingsMenu)}
                className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Settings
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showSettingsMenu && (
                <>
                  {/* Backdrop to close menu */}
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowSettingsMenu(false)}
                  />
                  {/* Dropdown menu */}
                  <div className="absolute right-0 mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                    <button
                      onClick={() => {
                        setShowSettingsMenu(false);
                        setShowEditModal(true);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Edit Site
                    </button>
                    <div className="border-t border-gray-100 my-1" />
                    <button
                      onClick={handleDeleteSite}
                      disabled={deleting}
                      className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 disabled:opacity-50"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      {deleting ? 'Deleting...' : 'Delete Site & All Data'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Primary Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-6">
          <nav className="flex gap-1">
            {PRIMARY_TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => {
                  if (!tab.comingSoon) {
                    onPluginChange(tab.id);
                  }
                }}
                disabled={tab.comingSoon}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activePrimaryTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : tab.comingSoon
                    ? 'border-transparent text-gray-400 cursor-not-allowed'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                }`}
              >
                <span className="flex items-center gap-2">
                  {tab.label}
                  {tab.comingSoon && (
                    <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">
                      Soon
                    </span>
                  )}
                </span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Secondary Tabs (Subtabs) */}
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="px-6">
          <nav className="flex gap-1">
            {visibleSubtabs.map(subtab => (
              <button
                key={subtab.id}
                onClick={() => !subtab.comingSoon && onContentTypeChange(subtab.id)}
                disabled={subtab.comingSoon}
                className={`px-3 py-2 text-sm font-medium rounded-t transition-colors ${
                  subtab.comingSoon
                    ? 'text-gray-400 cursor-not-allowed'
                    : activeSubtab === subtab.id
                    ? 'bg-white text-blue-600 border border-b-0 border-gray-200'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {subtab.label}
                {subtab.comingSoon && (
                  <span className="ml-1.5 text-xs bg-gray-200 text-gray-500 px-1.5 py-0.5 rounded">
                    Soon
                  </span>
                )}
              </button>
            ))}
            {visibleSubtabs.length === 0 && (
              <div className="px-3 py-2 text-sm text-gray-500">
                No content types enabled. Go to settings to enable more.
              </div>
            )}
          </nav>
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white border-b border-gray-200 px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Search */}
            <div className="relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={`Search ${currentSubtab?.label.toLowerCase() || 'content'}...`}
                className="pl-9 pr-4 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-64"
              />
            </div>

          </div>

          <div className="flex items-center gap-2">
            {/* Bulk actions */}
            <button className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg">
              Bulk Edit
            </button>
            <button className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg">
              Export
            </button>
          </div>
        </div>
      </div>

      {/* Data Grid Area */}
      <div className="flex-1 overflow-auto bg-white">
        {currentPrimaryTab.comingSoon ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900">{currentPrimaryTab.label} Coming Soon</h3>
              <p className="text-sm text-gray-500 mt-1">
                We're working on {currentPrimaryTab.label} integration. Check back soon!
              </p>
            </div>
          </div>
        ) : (
          <DataGrid
            siteId={siteId}
            contentType={activeSubtab}
            endpoint={currentSubtab?.endpoint || ''}
            searchQuery={searchQuery}
          />
        )}
      </div>

      {/* Sync Status Panel */}
      <SyncStatusPanel
        siteId={siteId}
        isOpen={showSyncStatus}
        onClose={() => setShowSyncStatus(false)}
        jobId={currentJobId}
      />

      {/* Edit Site Modal */}
      <EditSiteModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        siteId={siteId}
        currentName={siteName}
        currentUrl={siteUrl}
        currentWpUsername={wpUsername}
      />
    </div>
  );
}

// Column definition from Query Engine
interface ColumnDef {
  key: string;
  label: string;
  type: string;
  sortable: boolean;
  filterable: boolean;
  editable: boolean;
  width?: number;
}

interface ColumnConfig {
  columns: ColumnDef[];
  defaultColumns: string[];
  defaultSort: { column: string; direction: 'asc' | 'desc' };
}

interface QueryResult {
  data: Record<string, unknown>[];
  total: number;
  page: number;
  perPage: number;
  totalPages: number;
}

// Content types are already in the correct format (posts, pages, media, users)
// No conversion needed - they match what's stored in the database
function getObjectType(contentType: string): string {
  return contentType;
}

// Column Selector dropdown component
function ColumnSelector({
  allColumns,
  visibleKeys,
  onToggle,
  onReset,
  onShowAll,
  hasCustomPreferences,
}: {
  allColumns: ColumnDef[];
  visibleKeys: string[];
  onToggle: (key: string) => void;
  onReset?: () => void;
  onShowAll?: () => void;
  hasCustomPreferences?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const allVisible = visibleKeys.length === allColumns.length;

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`px-3 py-1.5 text-sm border rounded-lg flex items-center gap-1 ${
          hasCustomPreferences
            ? 'text-blue-600 border-blue-300 bg-blue-50 hover:bg-blue-100'
            : 'text-gray-600 hover:text-gray-900 border-gray-300'
        }`}
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
        </svg>
        Columns ({visibleKeys.length}/{allColumns.length})
        {hasCustomPreferences && <span className="text-xs">*</span>}
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 mt-1 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-20 max-h-96 overflow-y-auto">
            <div className="px-3 py-1 text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-100 mb-1 flex items-center justify-between">
              <span>Toggle Columns</span>
              <div className="flex gap-2">
                {!allVisible && onShowAll && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onShowAll();
                    }}
                    className="text-blue-600 hover:text-blue-800 normal-case font-normal"
                  >
                    Show All
                  </button>
                )}
                {hasCustomPreferences && onReset && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onReset();
                    }}
                    className="text-gray-500 hover:text-gray-700 normal-case font-normal"
                  >
                    Reset
                  </button>
                )}
              </div>
            </div>
            {allColumns.map((col) => (
              <label
                key={col.key}
                className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={visibleKeys.includes(col.key)}
                  onChange={() => onToggle(col.key)}
                  className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">{col.label}</span>
                <span className="text-xs text-gray-400 ml-auto">{col.type}</span>
              </label>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// Re-sync response type
interface ResyncResponse {
  success: boolean;
  message: string;
  results: { wpId: number; success: boolean; error?: string }[];
}

// DataGrid component that fetches and displays content using Query Engine
function DataGrid({ siteId, contentType, searchQuery }: {
  siteId: string;
  contentType: string;
  endpoint: string;
  searchQuery?: string;
}) {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [showFilters, setShowFilters] = useState(false);

  // Selection state
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [resyncingIds, setResyncingIds] = useState<Set<number>>(new Set());

  // Content view modal state
  const [contentModal, setContentModal] = useState<{
    isOpen: boolean;
    title: string;
    content: string;
  }>({ isOpen: false, title: '', content: '' });

  // Inline edit modal state
  const [editModal, setEditModal] = useState<{
    isOpen: boolean;
    item: {
      id: string;
      wp_id: number;
      title: string;
      metadata: Record<string, unknown>;
    } | null;
  }>({ isOpen: false, item: null });

  // Content editor modal state
  const [contentEditorModal, setContentEditorModal] = useState<{
    isOpen: boolean;
    item: {
      id: string;
      wp_id: number;
      title: string;
      metadata: Record<string, unknown>;
    } | null;
  }>({ isOpen: false, item: null });

  // Bulk edit modal state
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);

  // Inline cell edit state
  const [editingCell, setEditingCell] = useState<{
    wpId: number;
    columnKey: string;
    value: unknown;
  } | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);

  // Undo toast state
  const [undoToast, setUndoToast] = useState<{
    show: boolean;
    changesetId: string | null;
  }>({ show: false, changesetId: null });

  // Views state
  const [activeViewId, setActiveViewId] = useState<string | null>(null);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [editingView, setEditingView] = useState<View | null>(null);

  // Filter state
  const {
    filters,
    filterLogic,
    addFilter,
    updateFilter,
    removeFilter,
    clearFilters,
    setFilterLogic,
    setFilters,
    hasActiveFilters,
    toApiFilters,
  } = useFilters();

  const objectType = getObjectType(contentType);
  const perPage = 50;

  // Taxonomy lookup for categories/tags
  const taxonomyLookup = useTaxonomyLookup(siteId);

  // Author lookup for author names
  const { data: authors = [] } = useAuthors(siteId);
  const authorMap = useMemo(() => {
    const map = new Map<number, string>();
    authors.forEach(author => {
      const id = parseInt(author.value, 10);
      if (!isNaN(id)) {
        map.set(id, author.label);
      }
    });
    console.log('[SiteView] Author map:', Object.fromEntries(map));
    console.log('[SiteView] Authors data:', authors);
    return map;
  }, [authors]);

  // Re-sync mutation
  const resyncMutation = useMutation({
    mutationFn: async (wpIds: number[]) => {
      return api.post<ResyncResponse>(`/api/v1/sites/${siteId}/resync`, {
        objectType,
        wpIds,
      });
    },
    onSuccess: () => {
      // Invalidate the query to refetch updated data
      queryClient.invalidateQueries({ queryKey: ['site-content', siteId, objectType] });
      // Clear selection after successful sync
      setSelectedIds(new Set());
    },
  });

  // Handler to re-sync a single item
  const handleResyncItem = useCallback((wpId: number) => {
    setResyncingIds(prev => new Set(prev).add(wpId));
    resyncMutation.mutate([wpId], {
      onSettled: () => {
        setResyncingIds(prev => {
          const next = new Set(prev);
          next.delete(wpId);
          return next;
        });
      },
    });
  }, [resyncMutation]);

  // Handler to re-sync selected items
  const handleResyncSelected = useCallback(() => {
    const wpIds = Array.from(selectedIds);
    setResyncingIds(new Set(wpIds));
    resyncMutation.mutate(wpIds, {
      onSettled: () => {
        setResyncingIds(new Set());
      },
    });
  }, [selectedIds, resyncMutation]);

  // Toggle single row selection
  const toggleRowSelection = useCallback((wpId: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(wpId)) {
        next.delete(wpId);
      } else {
        next.add(wpId);
      }
      return next;
    });
  }, []);

  // Toggle all rows selection
  const toggleAllSelection = useCallback((allWpIds: number[]) => {
    setSelectedIds(prev => {
      if (prev.size === allWpIds.length) {
        return new Set();
      }
      return new Set(allWpIds);
    });
  }, []);

  // Inline cell editing handlers
  const startCellEdit = useCallback((wpId: number, columnKey: string, currentValue: unknown) => {
    setEditingCell({ wpId, columnKey, value: currentValue });
    setEditValue(String(currentValue || ''));
  }, []);

  const cancelCellEdit = useCallback(() => {
    setEditingCell(null);
    setEditValue('');
    setIsSaving(false);
  }, []);

  const saveCellEdit = useCallback(async () => {
    if (!editingCell) return;

    setIsSaving(true);
    try {
      // Call the edit API
      const response = await api.post<{ success: boolean; changesetId?: string }>(`/api/v1/sites/${siteId}/content/${objectType}/${editingCell.wpId}/edit`, {
        fields: {
          [editingCell.columnKey]: editValue,
        },
      });

      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ['site-content', siteId, objectType] });

      // Show undo toast
      if (response.changesetId) {
        setUndoToast({ show: true, changesetId: response.changesetId });
        // Auto-hide after 10 seconds
        setTimeout(() => {
          setUndoToast({ show: false, changesetId: null });
        }, 10000);
      }

      // Clear edit state
      cancelCellEdit();
    } catch (error) {
      console.error('Failed to save cell edit:', error);
      alert(error instanceof Error ? error.message : 'Failed to save changes');
      setIsSaving(false);
    }
  }, [editingCell, editValue, siteId, objectType, queryClient, cancelCellEdit]);

  // Clear selection when content type changes
  useEffect(() => {
    setSelectedIds(new Set());
  }, [objectType]);

  // Debounce search to avoid excessive API calls
  const debouncedSearch = useDebounce(searchQuery || '', 300);

  // Reset pagination when content type, search, or filters change
  useEffect(() => {
    setPage(1);
  }, [objectType, debouncedSearch, filters]);

  // Reset filters when object type changes
  useEffect(() => {
    clearFilters();
  }, [objectType, clearFilters]);

  // Fetch column config using React Query - handles caching and race conditions
  const {
    data: columnConfig,
    isLoading: configLoading,
  } = useQuery({
    queryKey: ['column-config', siteId, objectType],
    queryFn: async () => {
      try {
        return await api.get<ColumnConfig>(`/api/v1/sites/${siteId}/columns/${objectType}`);
      } catch {
        // Return null for unsupported types - will use fallback columns
        return null;
      }
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Use column preferences hook for persistence
  const {
    visibleColumns: activeColumnKeys,
    allColumns,
    toggleColumn: handleToggleColumn,
    resetToDefault,
    hasCustomPreferences,
    setVisibleColumns,
  } = useColumnPreferences({
    siteId,
    contentType: objectType,
    columnConfig: columnConfig as ColumnConfigType | null,
  });

  // Use views hook for saved views
  const {
    views,
    templates,
    isLoading: viewsLoading,
    createView,
    updateView,
    deleteView,
    isCreating: viewSaving,
  } = useViews({
    siteId,
    contentType: objectType,
  });

  // Set default sort when config loads
  useEffect(() => {
    if (columnConfig && !sortColumn) {
      setSortColumn(columnConfig.defaultSort.column);
      setSortDirection(columnConfig.defaultSort.direction);
    }
  }, [columnConfig, sortColumn]);

  // Reset sort when object type changes
  useEffect(() => {
    setSortColumn(null);
    setSortDirection('desc');
  }, [objectType]);

  // Reset active view when object type changes
  useEffect(() => {
    setActiveViewId(null);
    setEditingView(null);
  }, [objectType]);

  // Apply view settings when a view is selected
  const handleViewSelect = useCallback((view: View | null) => {
    if (!view) {
      // Selecting "All" - reset to defaults
      setActiveViewId(null);
      clearFilters();
      resetToDefault();
      if (columnConfig) {
        setSortColumn(columnConfig.defaultSort.column);
        setSortDirection(columnConfig.defaultSort.direction);
      }
      return;
    }

    // Apply view settings
    setActiveViewId(view.id);

    // Apply columns
    if (view.columns.length > 0) {
      setVisibleColumns(view.columns);
    }

    // Apply filters
    if (view.filters.length > 0) {
      const conditions = view.filters.map((f, i) => viewFilterToCondition(f, i));
      setFilters(conditions);
      setFilterLogic(view.filter_logic);
      setShowFilters(true);
    } else {
      clearFilters();
    }

    // Apply sort
    if (view.sort_column) {
      setSortColumn(view.sort_column);
      setSortDirection(view.sort_direction);
    }
  }, [clearFilters, columnConfig, resetToDefault, setFilterLogic, setFilters, setVisibleColumns]);

  // Calculate if there are unsaved changes compared to active view
  const hasUnsavedChanges = useMemo(() => {
    if (!activeViewId) {
      // When on "All", changes exist if any filters are applied or columns differ from defaults
      return hasActiveFilters || hasCustomPreferences;
    }

    const activeView = views.find(v => v.id === activeViewId);
    if (!activeView) return false;

    // Compare columns
    const columnsMatch = JSON.stringify(activeColumnKeys.slice().sort()) ===
      JSON.stringify(activeView.columns.slice().sort());

    // Compare filters (by value, ignoring ids)
    const currentApiFilters = toApiFilters();
    const filtersMatch = JSON.stringify(currentApiFilters) === JSON.stringify(activeView.filters);

    // Compare sort
    const sortMatch = sortColumn === activeView.sort_column && sortDirection === activeView.sort_direction;

    // Compare filter logic
    const logicMatch = filterLogic === activeView.filter_logic;

    return !columnsMatch || !filtersMatch || !sortMatch || !logicMatch;
  }, [activeViewId, views, activeColumnKeys, toApiFilters, sortColumn, sortDirection, filterLogic, hasActiveFilters, hasCustomPreferences]);

  // Handle save view
  const handleSaveView = useCallback(() => {
    if (activeViewId) {
      // Edit existing view
      const view = views.find(v => v.id === activeViewId);
      setEditingView(view || null);
    } else {
      // Create new view
      setEditingView(null);
    }
    setShowSaveDialog(true);
  }, [activeViewId, views]);

  // Handle save dialog submit
  const handleSaveDialogSubmit = useCallback(async (data: Parameters<typeof createView>[0] | Parameters<typeof updateView>[1]) => {
    if (editingView) {
      await updateView(editingView.id, data as Parameters<typeof updateView>[1]);
    } else {
      const newView = await createView(data as Parameters<typeof createView>[0]);
      setActiveViewId(newView.id);
    }
  }, [editingView, createView, updateView]);

  // Handle delete view
  const handleDeleteView = useCallback(async (view: View) => {
    if (!confirm(`Delete view "${view.name}"? This cannot be undone.`)) return;

    await deleteView(view.id);
    if (activeViewId === view.id) {
      setActiveViewId(null);
    }
  }, [deleteView, activeViewId]);

  // Helper to open content view modal
  const openContentModal = useCallback((title: string, content: string) => {
    setContentModal({ isOpen: true, title, content });
  }, []);

  // Helper to open inline edit modal for a row
  const openEditModal = useCallback((row: Record<string, unknown>) => {
    // Merge metadata JSONB with top-level row fields
    // IMPORTANT: metadata first, then row - so top-level columns (slug, status)
    // take precedence over any undefined/null values in metadata JSONB
    const metadata = row.metadata as Record<string, unknown> || {};
    const mergedData = { ...metadata, ...row };

    setEditModal({
      isOpen: true,
      item: {
        id: String(row.id || ''),
        wp_id: Number(row.wp_id || row._wpId || 0),
        title: String(row.title || ''),
        metadata: mergedData,
      },
    });
  }, []);

  // Get API-ready filters (strips id field)
  const apiFilters = toApiFilters();

  // Fetch data using React Query - automatically handles race conditions
  const {
    data: queryResult,
    isLoading: dataLoading,
    error: dataError,
  } = useQuery({
    queryKey: ['site-content', siteId, objectType, page, sortColumn, sortDirection, debouncedSearch, activeColumnKeys, apiFilters, filterLogic],
    queryFn: async () => {
      // Always include essential fields for editing
      // Only include top-level columns here: slug, status
      // Other fields (excerpt, categories, tags, etc.) are in metadata JSONB which is always returned
      const essentialEditFields = ['slug', 'status'];
      const allColumns = Array.from(new Set([...activeColumnKeys, ...essentialEditFields]));

      const result = await api.post<QueryResult>(
        `/api/v1/sites/${siteId}/query`,
        {
          objectType,
          columns: allColumns,
          filters: apiFilters.length > 0 ? apiFilters : undefined,
          sort: sortColumn ? { column: sortColumn, direction: sortDirection } : undefined,
          pagination: { page, perPage },
          search: debouncedSearch || undefined,
        }
      );
      return result;
    },
    enabled: !configLoading, // Wait for column config to load first
    staleTime: 30 * 1000, // Cache for 30 seconds
  });

  const data = queryResult?.data || [];
  const total = queryResult?.total || 0;
  const totalPages = queryResult?.totalPages || 1;
  const loading = configLoading || dataLoading;
  const error = dataError instanceof Error ? dataError.message : null;

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Get visible columns (allColumns comes from useColumnPreferences hook)
  // Fallback columns for when column config hasn't loaded yet
  type DisplayColumn = { key: string; label: string; type: string; sortable: boolean; filterable: boolean; editable: boolean; width?: number };
  const visibleColumns: DisplayColumn[] = allColumns.length > 0
    ? allColumns.filter(col => activeColumnKeys.includes(col.key))
    : [
        { key: 'id', label: 'ID', type: 'number', sortable: true, filterable: true, editable: false },
        { key: 'title', label: 'Title', type: 'text', sortable: true, filterable: true, editable: true },
        { key: 'status', label: 'Status', type: 'select', sortable: true, filterable: true, editable: true },
      ];

  if (loading && data.length === 0) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="p-6">
        <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 capitalize">No {contentType} synced yet</h3>
          <p className="text-sm text-gray-500 mt-1">
            Click the "Sync" button to fetch {contentType} from WordPress.
          </p>
        </div>
      </div>
    );
  }

  // Render cell value based on column type
  const renderCell = (row: Record<string, unknown>, column: ColumnDef) => {
    const value = row[column.key];
    const wpId = Number(row.wp_id || row._wpId || row.id);
    const isEditing = editingCell?.wpId === wpId && editingCell?.columnKey === column.key;
    const isEditable = column.editable && !['multiselect', 'image', 'richtext', 'boolean'].includes(column.type);

    // If this cell is being edited, show the edit UI
    if (isEditing) {
      // For author field, show dropdown instead of text input
      if (column.key === 'author') {
        return (
          <div className="flex items-center gap-2">
            <select
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveCellEdit();
                if (e.key === 'Escape') cancelCellEdit();
              }}
              autoFocus
              disabled={isSaving}
              className="flex-1 px-2 py-1 text-sm border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select author...</option>
              {authors.map(author => (
                <option key={author.value} value={author.value}>
                  {author.label}
                </option>
              ))}
            </select>
            <button
              onClick={saveCellEdit}
              disabled={isSaving}
              className="p-1 text-green-600 hover:bg-green-50 rounded transition-colors disabled:opacity-50"
              title="Save"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </button>
            <button
              onClick={cancelCellEdit}
              disabled={isSaving}
              className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
              title="Cancel"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        );
      }

      // Default text input for other fields
      return (
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') saveCellEdit();
              if (e.key === 'Escape') cancelCellEdit();
            }}
            autoFocus
            disabled={isSaving}
            className="flex-1 px-2 py-1 text-sm border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={saveCellEdit}
            disabled={isSaving}
            className="p-1 text-green-600 hover:bg-green-50 rounded transition-colors disabled:opacity-50"
            title="Save"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </button>
          <button
            onClick={cancelCellEdit}
            disabled={isSaving}
            className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
            title="Cancel"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      );
    }

    if (value === null || value === undefined) {
      return <span className="text-gray-400">—</span>;
    }

    // Render the cell content based on type
    let cellContent: React.ReactNode;

    switch (column.type) {
      case 'date':
        // Show relative time for sync_age column, regular date for others
        if (column.key === 'sync_age') {
          const { relative, absolute } = formatRelativeTime(String(value));
          cellContent = (
            <span title={absolute} className="cursor-help text-gray-600">
              {relative}
            </span>
          );
        } else {
          cellContent = new Date(String(value)).toLocaleDateString();
        }
        break;
      case 'boolean':
        cellContent = value ? '✓' : '✗';
        break;
      case 'select':
        cellContent = (
          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
            value === 'publish' || value === 'published' ? 'bg-green-100 text-green-700' :
            value === 'draft' ? 'bg-yellow-100 text-yellow-700' :
            value === 'trash' ? 'bg-red-100 text-red-700' :
            'bg-gray-100 text-gray-700'
          }`}>
            {String(value)}
          </span>
        );
        break;
      case 'image':
        cellContent = value ? (
          <img
            src={String(value)}
            alt=""
            className="w-10 h-10 object-cover rounded"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        ) : null;
        break;
      case 'multiselect':
        if (Array.isArray(value)) {
          cellContent = value.slice(0, 3).map((v, i) => {
            let displayValue: string;

            // Handle taxonomy lookups (categories/tags)
            if (column.key === 'categories' && typeof v === 'number') {
              displayValue = taxonomyLookup.getCategoryName(v);
            } else if (column.key === 'tags' && typeof v === 'number') {
              displayValue = taxonomyLookup.getTagName(v);
            } else if (typeof v === 'object' && v !== null && 'name' in v) {
              displayValue = (v as { name: string }).name;
            } else {
              displayValue = String(v);
            }

            return (
              <span key={i} className="inline-flex px-2 py-0.5 mr-1 text-xs bg-gray-100 rounded">
                {displayValue}
              </span>
            );
          });
        } else {
          cellContent = String(value);
        }
        break;
      case 'url':
        cellContent = (
          <a href={String(value)} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate max-w-xs block">
            {String(value).replace(/^https?:\/\//, '')}
          </a>
        );
        break;
      case 'richtext': {
        // Strip HTML and truncate for display
        const rawHtml = String(value);
        const plainText = stripHtml(rawHtml);
        const charCount = countCharacters(plainText);
        const displayText = truncateText(plainText, 100);
        const rowTitle = String(row.title || row.name || 'Content');

        cellContent = (
          <div className="flex items-center gap-2 max-w-md">
            <span className="text-gray-700 truncate flex-1" title={plainText}>
              {displayText}
            </span>
            {/* Character count badge */}
            {charCount > 0 && (
              <span className="text-xs text-gray-400 whitespace-nowrap">
                {charCount}
              </span>
            )}
            {/* View button */}
            {rawHtml && rawHtml.length > 0 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openContentModal(rowTitle, rawHtml);
                }}
                className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                title="View full content"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </button>
            )}
          </div>
        );
        break;
      }
      default: {
        // Handle author column - show display name instead of ID
        if (column.key === 'author') {
          console.log('[renderCell] Author value:', value, 'type:', typeof value);
          const authorId = typeof value === 'number' ? value : parseInt(String(value || '0'), 10);
          console.log('[renderCell] Parsed authorId:', authorId, 'authorMap has:', authorMap.get(authorId));
          if (authorId > 0) {
            const authorName = authorMap.get(authorId);
            cellContent = authorName || `Author #${authorId}`;
          } else {
            cellContent = <span className="text-gray-400">No author</span>;
          }
        } else {
          // Decode HTML entities (e.g., &#8211; -> –) for clean display
          const decoded = decodeHtmlEntities(String(value));
          cellContent = decoded.length > 100 ? decoded.slice(0, 100) + '...' : decoded;
        }
        break;
      }
    }

    // Wrap with pencil icon if editable
    if (isEditable) {
      return (
        <div className="group flex items-center justify-between gap-2">
          <div className="flex-1">{cellContent}</div>
          <button
            onClick={() => startCellEdit(wpId, column.key, value)}
            className="opacity-30 hover:opacity-100 transition-opacity p-1 hover:bg-gray-100 rounded"
            title="Edit"
          >
            <svg className="w-3.5 h-3.5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
          </button>
        </div>
      );
    }

    return cellContent;
  };

  // Get filterable columns for FilterBar
  const filterableColumns = allColumns.filter(col => col.filterable) as ColumnDefinition[];

  return (
    <div className="flex flex-col h-full">
      {/* Views bar */}
      {allColumns.length > 0 && (
        <ViewSelector
          views={views}
          templates={templates}
          activeViewId={activeViewId}
          onViewSelect={handleViewSelect}
          onSaveView={handleSaveView}
          onDeleteView={handleDeleteView}
          hasUnsavedChanges={hasUnsavedChanges}
          isLoading={viewsLoading}
        />
      )}

      {/* Table toolbar with filter toggle and column selector */}
      {allColumns.length > 0 && (
        <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-3 py-1.5 text-sm border rounded-lg flex items-center gap-1.5 transition-colors ${
                hasActiveFilters
                  ? 'text-blue-600 border-blue-300 bg-blue-50 hover:bg-blue-100'
                  : showFilters
                  ? 'text-gray-900 border-gray-400 bg-gray-100'
                  : 'text-gray-600 hover:text-gray-900 border-gray-300 hover:bg-gray-50'
              }`}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              Filter
              {hasActiveFilters && (
                <span className="px-1.5 py-0.5 text-xs bg-blue-600 text-white rounded-full">
                  {filters.length}
                </span>
              )}
            </button>

            {/* Bulk actions - shown when items are selected */}
            {selectedIds.size > 0 && (
              <>
                <div className="h-5 w-px bg-gray-300" />
                <span className="text-sm text-gray-600">
                  {selectedIds.size} selected
                </span>
                <button
                  onClick={handleResyncSelected}
                  disabled={resyncMutation.isPending}
                  className="px-3 py-1.5 text-sm border border-green-300 bg-green-50 text-green-700 hover:bg-green-100 rounded-lg flex items-center gap-1.5 transition-colors disabled:opacity-50"
                >
                  <svg className={`w-4 h-4 ${resyncMutation.isPending ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  {resyncMutation.isPending ? 'Syncing...' : 'Re-sync Selected'}
                </button>
                <button
                  onClick={() => setSelectedIds(new Set())}
                  className="px-2 py-1.5 text-sm text-gray-500 hover:text-gray-700"
                >
                  Clear
                </button>
              </>
            )}
          </div>
          <ColumnSelector
            allColumns={allColumns}
            visibleKeys={activeColumnKeys}
            onToggle={handleToggleColumn}
            onReset={resetToDefault}
            onShowAll={() => setVisibleColumns(allColumns.map(c => c.key))}
            hasCustomPreferences={hasCustomPreferences}
          />
        </div>
      )}

      {/* Filter Bar (collapsible) */}
      {showFilters && filterableColumns.length > 0 && (
        <div className="px-4 py-3 bg-zinc-900 border-b border-zinc-700">
          <FilterBar
            columns={filterableColumns}
            filters={filters}
            filterLogic={filterLogic}
            siteId={siteId}
            onFiltersChange={setFilters}
            onFilterLogicChange={setFilterLogic}
            onAddFilter={addFilter}
            onUpdateFilter={updateFilter}
            onRemoveFilter={removeFilter}
            onClearFilters={clearFilters}
          />
        </div>
      )}

      {/* Selection Action Bar */}
      {selectedIds.size > 0 && (
        <div className="bg-blue-50 border-b border-blue-200 px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-blue-700">
              {selectedIds.size} item{selectedIds.size !== 1 ? 's' : ''} selected
            </span>
            <button
              onClick={() => setSelectedIds(new Set())}
              className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
            >
              Clear selection
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowBulkEditModal(true)}
              className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg"
            >
              Bulk Edit
            </button>
            <button
              onClick={handleResyncSelected}
              disabled={resyncMutation.isPending}
              className="px-3 py-1.5 text-sm font-medium text-blue-600 bg-white border border-blue-300 hover:bg-blue-50 rounded-lg disabled:opacity-50"
            >
              {resyncMutation.isPending ? 'Re-syncing...' : 'Re-sync Selected'}
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              {/* Checkbox column header */}
              <th className="px-3 py-3 w-10">
                <input
                  type="checkbox"
                  checked={data.length > 0 && selectedIds.size === data.length}
                  onChange={() => toggleAllSelection(data.map(row => Number(row.wp_id || row._wpId || row.id)))}
                  className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                  title={selectedIds.size === data.length ? 'Deselect all' : 'Select all'}
                />
              </th>
              {visibleColumns.map((col) => (
                <th
                  key={col.key}
                  onClick={() => col.sortable && handleSort(col.key)}
                  className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                    col.sortable ? 'cursor-pointer hover:bg-gray-100' : ''
                  }`}
                  style={{ width: col.width || 'auto' }}
                >
                  <div className="flex items-center gap-1">
                    {col.label}
                    {col.sortable && sortColumn === col.key && (
                      <span className="text-blue-600">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
              ))}
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((row, idx) => {
              const wpId = Number(row.wp_id || row._wpId || row.id);
              const isResyncing = resyncingIds.has(wpId);
              const isSelected = selectedIds.has(wpId);

              return (
                <tr
                  key={String(wpId || idx)}
                  className={`hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''} ${isResyncing ? 'opacity-60' : ''}`}
                >
                  {/* Checkbox cell */}
                  <td className="px-3 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleRowSelection(wpId)}
                      className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                  </td>
                  {visibleColumns.map((col) => (
                    <td key={col.key} className="px-4 py-3 text-sm text-gray-900">
                      {renderCell(row, col)}
                    </td>
                  ))}
                  <td className="px-4 py-3 text-sm">
                    <div className="flex items-center gap-2">
                      {/* Refresh/Re-sync button */}
                      <button
                        onClick={() => handleResyncItem(wpId)}
                        disabled={isResyncing}
                        className="p-1 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors disabled:opacity-50"
                        title="Re-sync from WordPress"
                      >
                        <svg className={`w-4 h-4 ${isResyncing ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                      </button>
                      {/* Edit button */}
                      <button
                        onClick={() => openEditModal(row)}
                        className="text-blue-600 hover:text-blue-800 text-sm hover:underline"
                      >
                        Edit
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="bg-white border-t border-gray-200 px-4 py-3 flex items-center justify-between">
        <div className="text-sm text-gray-500">
          Showing {((page - 1) * perPage) + 1} to {Math.min(page * perPage, total)} of {total} {contentType}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Previous
          </button>
          <span className="text-sm text-gray-600">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Next
          </button>
        </div>
      </div>

      {/* Save View Dialog */}
      <SaveViewDialog
        isOpen={showSaveDialog}
        onClose={() => {
          setShowSaveDialog(false);
          setEditingView(null);
        }}
        onSave={handleSaveDialogSubmit}
        siteId={siteId}
        contentType={objectType}
        columns={activeColumnKeys}
        filters={toApiFilters()}
        filterLogic={filterLogic}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        existingView={editingView}
        isSaving={viewSaving}
      />

      {/* Content View Modal */}
      <ContentViewModal
        isOpen={contentModal.isOpen}
        onClose={() => setContentModal({ isOpen: false, title: '', content: '' })}
        title={contentModal.title}
        content={contentModal.content}
        contentType="html"
      />

      {/* Inline Edit Modal */}
      {editModal.item && (
        <InlineEditModal
          isOpen={editModal.isOpen}
          onClose={() => setEditModal({ isOpen: false, item: null })}
          siteId={siteId}
          contentType={objectType as 'posts' | 'pages' | 'media' | 'users'}
          item={editModal.item}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['site-content', siteId, objectType] });
            setEditModal({ isOpen: false, item: null });
          }}
          onEditContent={() => {
            // Open content editor modal with the same item
            setContentEditorModal({
              isOpen: true,
              item: editModal.item,
            });
          }}
        />
      )}

      {/* Content Editor Modal */}
      {contentEditorModal.item && (objectType === 'posts' || objectType === 'pages') && (
        <ContentEditorModal
          isOpen={contentEditorModal.isOpen}
          onClose={() => setContentEditorModal({ isOpen: false, item: null })}
          siteId={siteId}
          contentType={objectType as 'posts' | 'pages'}
          item={contentEditorModal.item}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['site-content', siteId, objectType] });
            setContentEditorModal({ isOpen: false, item: null });
          }}
        />
      )}

      {/* Bulk Edit Modal */}
      <BulkEditModal
        isOpen={showBulkEditModal}
        onClose={() => setShowBulkEditModal(false)}
        siteId={siteId}
        contentType={objectType as 'posts' | 'pages' | 'media' | 'users'}
        selectedItems={data
          .filter(row => selectedIds.has(Number(row.wp_id || row._wpId || row.id)))
          .map(row => ({
            id: String(row.id || ''),
            wp_id: Number(row.wp_id || row._wpId || 0),
            title: String(row.title || ''),
          }))}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['site-content', siteId, objectType] });
          setSelectedIds(new Set());
          setShowBulkEditModal(false);
        }}
      />

      {/* Undo Toast */}
      {undoToast.show && (
        <div className="fixed bottom-6 right-6 z-50 animate-slide-up">
          <div className="bg-gray-900 text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-3">
            <svg className="w-5 h-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-sm">Changes saved successfully</span>
            <button
              onClick={() => {
                // TODO: Implement undo API endpoint
                console.log('Undo changeset:', undoToast.changesetId);
                alert('Undo feature coming soon! Changeset ID: ' + undoToast.changesetId);
              }}
              className="ml-2 px-3 py-1 text-sm font-medium text-gray-900 bg-white hover:bg-gray-100 rounded transition-colors"
            >
              Undo
            </button>
            <button
              onClick={() => setUndoToast({ show: false, changesetId: null })}
              className="ml-1 p-1 hover:bg-gray-800 rounded transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
